import { useState, useEffect } from 'react';
import { reportService } from '../api/reports';
import { useAuth } from '../auth/AuthProvider';
import { Users, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '../components/ErrorBoundary';
import { useNavigate } from 'react-router-dom';

export default function TutorsPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tutors, setTutors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchTutors = async () => {
            try {
                // We restart the dashboard stats to get tutor data including efficiency and report counts
                const data = await reportService.getDashboardStats();
                setTutors(data.tutor_stats || []);
            } catch (error) {
                console.error("Failed to fetch tutors", error);
                setError(error.message || "Failed to load tutors");
            } finally {
                setLoading(false);
            }
        };

        if (user?.role === 'head' || user?.role === 'vice') {
            fetchTutors();
        } else {
            setError(`Role mismatch: ${user?.role}`);
            setLoading(false);
        }
    }, [user]);

    const filteredTutors = tutors.filter(tutor =>
        tutor.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tutor.first_name && tutor.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (tutor.last_name && tutor.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) return <div className="p-8 text-center text-gray-500">{t('loading_tutors')}</div>;

    if (error) return (
        <div className="p-8 bg-red-50 text-red-600 rounded-lg">
            <h3 className="font-bold">{t('error_loading')}</h3>
            <p>{JSON.stringify(error)}</p>
        </div>
    );

    return (
        <ErrorBoundary>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('tutors_title')}</h1>
                        <p className="text-sm text-gray-500 mt-1">{t('tutors_subtitle')}</p>
                    </div>

                    <div className="relative">
                        <input
                            type="text"
                            placeholder={t('search_placeholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full md:w-64"
                        />
                        <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50/50 text-xs uppercase tracking-wider text-gray-500 font-semibold border-b border-gray-100">
                                    <th className="px-6 py-4">{t('table_tutor')}</th>
                                    <th className="px-6 py-4 text-center">{t('table_total')}</th>
                                    <th className="px-6 py-4 text-center">{t('table_approved')}</th>
                                    <th className="px-6 py-4 text-center">{t('table_efficiency')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredTutors.length > 0 ? (
                                    filteredTutors.map((tutor) => {
                                        const successRate = tutor.total_reports > 0
                                            ? Math.round((tutor.approved_reports / tutor.total_reports) * 100)
                                            : 0;

                                        return (
                                            <tr
                                                key={tutor.id}
                                                onClick={() => navigate(`/tutors/${tutor.id}`)}
                                                className="hover:bg-gray-50/80 transition-colors cursor-pointer"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-700 mr-3 shadow-sm border border-primary-200">
                                                            {tutor.username?.[0]?.toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium text-gray-900">{tutor.username}</div>
                                                            <div className="text-xs text-gray-500">{tutor.first_name} {tutor.last_name}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center font-medium text-gray-700">
                                                    {tutor.total_reports}
                                                </td>
                                                <td className="px-6 py-4 text-center font-medium text-green-600">
                                                    {tutor.approved_reports}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${successRate >= 80 ? 'bg-green-100 text-green-800' :
                                                        successRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {successRate}%
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex flex-col items-center justify-center">
                                                <Users className="w-12 h-12 text-gray-200 mb-3" />
                                                <p>{t('tutors_not_found')}</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
}
